import React, { useEffect } from 'react';
import { useParams } from 'react-router';
import { DetailEpisode } from 'types/Asset';
import GSApi from 'api/Api';
import { SeriesDetailPropType } from 'types/SeriesDetails';
import { PageSeriesDetail } from 'components/Pages/PageSeriesDetail/PageSeriesDetail';
import { getLocalStorage, setLocalStorage } from 'utils/fnStorage';
import { useAuth } from 'providers/useAuth/AuthContext';
import { useDataFetcher } from 'hooks/useDataFetcher/useDataFetcher';
import { useGlobalNetworkError, useGlobalNoInternet } from 'hooks/withNetworkCheck/withNetworkCheck';
import { FetchParamById } from 'types/ApiTypes';
import VODSeriesDetailsHeader from './components/VODSeriesDetailsHeader';

const PAGE_ID = 'SERIES_DETAIL';
const LAST_COMPLETED_EPISODE_KEY = 'vod_last_completed_episode';

export const setLastCompletedEpisode = (seriesId: string, episodeId: string) => {
    const data = getLocalStorage(LAST_COMPLETED_EPISODE_KEY) || {};

    const thirtyDaysAgo = Date.now() - 30 * 24 * 60 * 60 * 1000;
    Object.keys(data).forEach((key) => {
        if (data[key].timestamp < thirtyDaysAgo) {
            delete data[key];
        }
    });

    data[seriesId] = {
        episodeId,
        timestamp: Date.now(),
    };

    setLocalStorage(LAST_COMPLETED_EPISODE_KEY, data);
};

const PageVODSeriesDetail: React.FC<SeriesDetailPropType> = ({ match }) => {
    const { id: routeId } = useParams<{ id: string }>();
    const { onNoInternet } = useGlobalNoInternet();
    const { onNetworkError } = useGlobalNetworkError();

    const seriesId = match?.params?.id ?? routeId;
    const id = seriesId;

    const { cacheKey } = useAuth();

    const {
        response: nextEpisode,
        hasLoaded: nextEpisodeLoaded,
        fetcher: fetchNextEpisode,
    } = useDataFetcher<DetailEpisode, FetchParamById>(
        (param) => GSApi.fetchVodSeriesNextEpisode(param.id, (data) => data),
        onNoInternet,
        onNetworkError
    );

    useEffect(() => {
        fetchNextEpisode({ id: seriesId });
    }, [seriesId, cacheKey]);

    const nextEpisodeReady = nextEpisodeLoaded;

    return (
        <PageSeriesDetail
            id={id}
            type={'VOD'}
            configId={PAGE_ID}
            screenName={'VODSeriesDetails'}
            pageHeader={(data) => (
                <VODSeriesDetailsHeader
                    series={data.series}
                    episode={data.episode}
                    overlayActive={data.overlayActive}
                    setOverlayActive={data.setOverlayActive}
                />
            )}
            fetchVODAssetDetails={GSApi.fetchVodEpisodeDetails}
            fetchVodSeriesDetails={GSApi.fetchVodSeriesDetails}
            fetchEpisodes={GSApi.fetchVodSeriesEpisodes}
            nextEpisode={nextEpisode}
            nextEpisodeReady={nextEpisodeReady}
        />
    );
};

export default PageVODSeriesDetail;
